<template>
  <div @keydown="handleKeydown" tabindex="0" class="calc-root">
    <h1 class="topcalc">Calculator</h1>

    <!-- Tab bar -->
    <div class="tab-bar">
      <button class="tab-btn" :class="{ 'tab-active': activeTab === 'calc' }"   @click="activeTab = 'calc'">Calculator</button>
      <button class="tab-btn" :class="{ 'tab-active': activeTab === 'relay' }"  @click="activeTab = 'relay'">Relay</button>
      <button class="tab-btn" :class="{ 'tab-active': activeTab === 'speed' }"  @click="switchTab('speed')">Speed Round</button>
      <button class="tab-btn" :class="{ 'tab-active': activeTab === 'target' }" @click="activeTab = 'target'">Target Number</button>
      <button class="tab-btn" :class="{ 'tab-active': activeTab === 'broken' }" @click="activeTab = 'broken'">Broken Calc</button>
    </div>

    <!-- ══════════════ CALCULATOR TAB ══════════════ -->
    <div v-if="activeTab === 'calc'" class="calc-tab-content">
      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-12 calculator">
          <!-- Expression display -->
          <div class="display-expr">{{ expression || '&nbsp;' }}</div>
          <!-- Main display -->
          <div class="display">{{ current || '0' }}</div>
          <!-- History -->
          <div class="history-panel" v-if="history.length">
            <div v-for="(h, i) in history" :key="i" class="history-row">{{ h }}</div>
          </div>
          <div @click="clearAll"   class="btn btn-fn">AC</div>
          <div @click="sign"       class="btn btn-fn">+/−</div>
          <div @click="percent"    class="btn btn-fn">%</div>
          <div @click="divide"     class="btn operator">÷</div>
          <div @click="append('7')" class="btn">7</div>
          <div @click="append('8')" class="btn">8</div>
          <div @click="append('9')" class="btn">9</div>
          <div @click="times"      class="btn operator">×</div>
          <div @click="append('4')" class="btn">4</div>
          <div @click="append('5')" class="btn">5</div>
          <div @click="append('6')" class="btn">6</div>
          <div @click="minus"      class="btn operator">−</div>
          <div @click="append('1')" class="btn">1</div>
          <div @click="append('2')" class="btn">2</div>
          <div @click="append('3')" class="btn">3</div>
          <div @click="add"        class="btn operator">+</div>
          <div @click="append('0')" class="btn zero">0</div>
          <div @click="dot"        class="btn">.</div>
          <div @click="equal"      class="btn operator">=</div>
        </div>
      </div>
      <div class="calcbord">
        <h2 style="margin-bottom:10px;text-align:center;">Calculator Tips</h2>
        <ul class="tips-list">
          <li><strong>AC</strong> - Clears everything</li>
          <li><strong>+/−</strong> - Toggles positive / negative</li>
          <li><strong>%</strong> - Converts to percentage (÷ 100)</li>
          <li><strong>÷ × − +</strong> - Arithmetic operators</li>
          <li><strong>=</strong> - Calculates the result</li>
          <li><strong>.</strong> - Decimal point</li>
          <li><strong>Keyboard</strong> - You can type numbers and operators directly</li>
        </ul>
      </div>
    </div>

    <!-- ══════════════ RELAY TAB ══════════════ -->
    <div v-if="activeTab === 'relay'" class="game-wrapper">
      <div class="text-center pill-row">
        <button v-for="d in relayDifficulties" :key="d.label" class="pill-btn"
          :class="{ active: relayDifficulty === d.label }" @click="setRelayDifficulty(d)">{{ d.label }}</button>
      </div>
      <div v-if="relayState === 'idle'" class="game-card">
        <h2 class="game-card-title">How It Works</h2>
        <p class="game-p">You'll be given a <strong>starting number</strong> and a chain of operations. Use the Calculator tab to compute each step, then come back and enter your result.</p>
        <button class="btn game-start-btn" @click="startRelay">Start Relay</button>
      </div>
      <div v-if="relayState === 'playing'" class="game-card">
        <div class="step-progress">
          <div v-for="(s, i) in relaySteps" :key="i" class="step-dot"
            :class="{ 'dot-done': i < currentStepIndex, 'dot-current': i === currentStepIndex, 'dot-pending': i > currentStepIndex }"></div>
        </div>
        <div class="relay-start-num">
          <span class="relay-label">{{ currentStepIndex === 0 ? 'Start with:' : 'Current value:' }}</span>
          <span class="relay-big-num">{{ confirmedValue }}</span>
        </div>
        <div class="step-instruction">
          <span class="step-counter">Step {{ currentStepIndex + 1 }} of {{ relaySteps.length }}</span>
          <div class="step-op-display">
            <span class="step-op-symbol">{{ relaySteps[currentStepIndex].opSymbol }}</span>
            <span class="step-op-num">{{ relaySteps[currentStepIndex].operand }}</span>
          </div>
          <p class="step-hint">{{ confirmedValue }} {{ relaySteps[currentStepIndex].opSymbol }} {{ relaySteps[currentStepIndex].operand }}</p>
        </div>
        <div class="answer-row">
          <input ref="relayInput" type="number" class="game-input" v-model="relayUserInput"
            placeholder="Enter result" @keyup.enter="checkRelayStep" />
          <button class="btn game-check-btn" @click="checkRelayStep">Check</button>
        </div>
        <transition name="fade">
          <p v-if="relayFeedback" class="game-feedback" :class="relayFeedbackClass">{{ relayFeedback }}</p>
        </transition>
      </div>
      <div v-if="relayState === 'complete'" class="game-card game-complete">
        <div class="complete-icon">🎉</div>
        <h2>Relay Complete!</h2>
        <p class="game-p">Final answer: <strong>{{ confirmedValue }}</strong></p>
        <div class="summary-table">
          <div v-for="(s, i) in relaySteps" :key="i" class="summary-row">
            <span class="summary-step">Step {{ i + 1 }}</span>
            <span class="summary-expr">{{ s.before }} {{ s.opSymbol }} {{ s.operand }} = {{ s.result }}</span>
          </div>
        </div>
        <button class="btn game-start-btn" @click="startRelay">Play Again</button>
      </div>
    </div>

    <!-- ══════════════ SPEED ROUND TAB ══════════════ -->
    <div v-if="activeTab === 'speed'" class="game-wrapper">
      <div class="text-center pill-row">
        <button v-for="d in speedDifficulties" :key="d.label" class="pill-btn"
          :class="{ active: speedDifficulty === d.label }" @click="setSpeedDifficulty(d)">{{ d.label }}</button>
      </div>
      <div v-if="speedState === 'idle'" class="game-card">
        <h2 class="game-card-title">Speed Round</h2>
        <p class="game-p">Solve as many questions as you can before the timer runs out. Use the <strong>Calculator</strong> tab to compute each answer, then come back and submit it here.</p>
        <p class="game-p"><strong>{{ speedConfig.time }}s</strong> on the clock. Go!</p>
        <button class="btn game-start-btn" @click="startSpeed">Start</button>
      </div>
      <div v-if="speedState === 'playing'" class="game-card">
        <!-- Timer bar -->
        <div class="timer-track">
          <div class="timer-fill" :style="{ width: timerPercent + '%' }"
            :class="{ 'timer-warn': timeLeft <= 10 }"></div>
        </div>
        <div class="timer-label">{{ timeLeft }}s</div>
        <div class="speed-question">{{ speedQuestion }}</div>
        <div class="answer-row">
          <input ref="speedInput" type="number" class="game-input" v-model="speedUserInput"
            placeholder="Answer" @keyup.enter="checkSpeed" />
          <button class="btn game-check-btn" @click="checkSpeed">Submit</button>
        </div>
        <transition name="fade">
          <p v-if="speedFeedback" class="game-feedback" :class="speedFeedbackClass">{{ speedFeedback }}</p>
        </transition>
        <p class="speed-score-live">Score: <strong>{{ speedScore }}</strong></p>
      </div>
      <div v-if="speedState === 'done'" class="game-card game-complete">
        <div class="complete-icon">⏱️</div>
        <h2>Time's Up!</h2>
        <p class="game-p">You answered <strong>{{ speedScore }}</strong> question{{ speedScore !== 1 ? 's' : '' }} correctly.</p>
        <button class="btn game-start-btn" @click="startSpeed">Play Again</button>
      </div>
    </div>

    <!-- ══════════════ TARGET NUMBER TAB ══════════════ -->
    <div v-if="activeTab === 'target'" class="game-wrapper">
      <div class="text-center pill-row">
        <button v-for="d in targetDifficulties" :key="d.label" class="pill-btn"
          :class="{ active: targetDifficulty === d.label }" @click="setTargetDifficulty(d)">{{ d.label }}</button>
      </div>
      <div v-if="targetState === 'idle'" class="game-card">
        <h2 class="game-card-title">Target Number</h2>
        <p class="game-p">You'll be given a <strong>starting number</strong>, a set of available numbers, and a <strong>target</strong>. Use any combination of the available numbers and operators to reach the target in limited presses.</p>
        <button class="btn game-start-btn" @click="startTarget">Start</button>
      </div>
      <div v-if="targetState === 'playing'" class="game-card">
        <div class="target-meta">
          <div class="target-box">
            <span class="target-label">Target</span>
            <span class="target-num">{{ targetGoal }}</span>
          </div>
          <div class="target-box">
            <span class="target-label">Presses left</span>
            <span class="target-num" :class="{ 'presses-warn': targetPressesLeft <= 3 }">{{ targetPressesLeft }}</span>
          </div>
        </div>
        <p class="game-p" style="margin-bottom:6px;">Available numbers: <strong>{{ targetNumbers.join('  ') }}</strong></p>
        <p class="game-p">Starting value: <strong>{{ targetStart }}</strong></p>
        <p class="game-p step-hint">Use the Calculator tab - then enter your result when done.</p>
        <div class="answer-row">
          <input ref="targetInput" type="number" class="game-input" v-model="targetUserInput"
            placeholder="Your result" @keyup.enter="checkTarget" />
          <button class="btn game-check-btn" @click="checkTarget">Check</button>
        </div>
        <transition name="fade">
          <p v-if="targetFeedback" class="game-feedback" :class="targetFeedbackClass">{{ targetFeedback }}</p>
        </transition>
        <button class="btn btn-skip" @click="startTarget" style="margin-top:14px;">Skip</button>
      </div>
      <div v-if="targetState === 'won'" class="game-card game-complete">
        <div class="complete-icon">🎯</div>
        <h2>Target Reached!</h2>
        <p class="game-p">You hit <strong>{{ targetGoal }}</strong> with {{ targetConfig.presses - targetPressesLeft }} press{{ (targetConfig.presses - targetPressesLeft) !== 1 ? 'es' : '' }}.</p>
        <button class="btn game-start-btn" @click="startTarget">Next Challenge</button>
      </div>
      <div v-if="targetState === 'lost'" class="game-card game-over">
        <div class="complete-icon">❌</div>
        <h2>Out of Presses!</h2>
        <p class="game-p">The target was <strong>{{ targetGoal }}</strong>.</p>
        <button class="btn game-start-btn" @click="startTarget">Try Again</button>
      </div>
    </div>

    <!-- ══════════════ BROKEN CALCULATOR TAB ══════════════ -->
    <div v-if="activeTab === 'broken'" class="game-wrapper">
      <div class="text-center pill-row">
        <button v-for="d in brokenDifficulties" :key="d.label" class="pill-btn"
          :class="{ active: brokenDifficulty === d.label }" @click="setBrokenDifficulty(d)">{{ d.label }}</button>
      </div>
      <div v-if="brokenState === 'idle'" class="game-card">
        <h2 class="game-card-title">Broken Calculator</h2>
        <p class="game-p">Some keys are <strong>broken</strong> (disabled). You must still reach the target using only the working keys. Get creative!</p>
        <button class="btn game-start-btn" @click="startBroken">Start</button>
      </div>
      <div v-if="brokenState === 'playing'" class="game-card">
        <div class="target-meta">
          <div class="target-box">
            <span class="target-label">Target</span>
            <span class="target-num">{{ brokenTarget }}</span>
          </div>
        </div>
        <p class="game-p">Broken keys: <span v-for="k in brokenKeys" :key="k" class="broken-key-badge">{{ k }}</span></p>
        <!-- Broken calculator grid -->
        <div class="calculator broken-calc">
          <div class="display">{{ brokenCurrent || '0' }}</div>
          <div @click="bCalcFn('clear')" class="btn btn-fn">AC</div>
          <div @click="bCalcFn('sign')"  class="btn btn-fn" :class="{ broken: isBroken('+/-') }">+/−</div>
          <div @click="bCalcFn('pct')"   class="btn btn-fn" :class="{ broken: isBroken('%') }">%</div>
          <div @click="bCalcOp('÷')"     class="btn operator" :class="{ broken: isBroken('÷') }">÷</div>
          <div v-for="n in [7,8,9]" :key="n" @click="bCalcAppend(String(n))" class="btn"
            :class="{ broken: isBroken(String(n)) }">{{ n }}</div>
          <div @click="bCalcOp('×')"     class="btn operator" :class="{ broken: isBroken('×') }">×</div>
          <div v-for="n in [4,5,6]" :key="n" @click="bCalcAppend(String(n))" class="btn"
            :class="{ broken: isBroken(String(n)) }">{{ n }}</div>
          <div @click="bCalcOp('−')"     class="btn operator" :class="{ broken: isBroken('−') }">−</div>
          <div v-for="n in [1,2,3]" :key="n" @click="bCalcAppend(String(n))" class="btn"
            :class="{ broken: isBroken(String(n)) }">{{ n }}</div>
          <div @click="bCalcOp('+')"     class="btn operator" :class="{ broken: isBroken('+') }">+</div>
          <div @click="bCalcAppend('0')" class="btn zero" :class="{ broken: isBroken('0') }">0</div>
          <div @click="bCalcDot"         class="btn" :class="{ broken: isBroken('.') }">.</div>
          <div @click="bCalcEqual"       class="btn operator">=</div>
        </div>
        <div class="answer-row" style="margin-top:14px;">
          <button class="btn game-check-btn" @click="checkBroken">Submit Answer</button>
        </div>
        <transition name="fade">
          <p v-if="brokenFeedback" class="game-feedback" :class="brokenFeedbackClass">{{ brokenFeedback }}</p>
        </transition>
        <button class="btn btn-skip" @click="startBroken" style="margin-top:10px;">New Puzzle</button>
      </div>
      <div v-if="brokenState === 'won'" class="game-card game-complete">
        <div class="complete-icon">🔧</div>
        <h2>Fixed It!</h2>
        <p class="game-p">You reached <strong>{{ brokenTarget }}</strong> despite the broken keys!</p>
        <button class="btn game-start-btn" @click="startBroken">Next Puzzle</button>
      </div>
    </div>

  </div>
</template>

<script>
import swal from "sweetalert";

function rand(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }

function applyOp(v, op, n) {
  switch(op) {
    case '+': return v + n;
    case '−': return v - n;
    case '×': return v * n;
    case '÷': return n === 0 ? null : v / n;
    default:  return v;
  }
}

const RELAY_DIFFICULTIES = [
  { label: 'Easy',   steps: 3, ops: ['+','−'],         numMin: 1, numMax: 20, startMin: 5,  startMax: 30  },
  { label: 'Medium', steps: 4, ops: ['+','−','×'],      numMin: 1, numMax: 50, startMin: 10, startMax: 50  },
  { label: 'Hard',   steps: 5, ops: ['+','−','×','÷'],  numMin: 1, numMax: 100,startMin: 10, startMax: 100 },
];
const SPEED_DIFFICULTIES = [
  { label: 'Easy',   time: 60, ops: ['+','−'],        min: 1,  max: 20  },
  { label: 'Medium', time: 45, ops: ['+','−','×'],     min: 1,  max: 50  },
  { label: 'Hard',   time: 30, ops: ['+','−','×','÷'], min: 2,  max: 12  },
];
const TARGET_DIFFICULTIES = [
  { label: 'Easy',   targetMin: 10,  targetMax: 50,  numbersCount: 4, presses: 6,  min: 1,  max: 10  },
  { label: 'Medium', targetMin: 50,  targetMax: 200, numbersCount: 5, presses: 8,  min: 1,  max: 25  },
  { label: 'Hard',   targetMin: 100, targetMax: 500, numbersCount: 6, presses: 10, min: 1,  max: 50  },
];
const BROKEN_DIFFICULTIES = [
  { label: 'Easy',   brokenCount: 2, targetMin: 5,  targetMax: 30  },
  { label: 'Medium', brokenCount: 3, targetMin: 10, targetMax: 100 },
  { label: 'Hard',   brokenCount: 5, targetMin: 20, targetMax: 200 },
];
const BREAKABLE = ['1','2','3','4','5','6','7','8','9','0','+','−','×','÷'];

export default {
  data() {
    return {
      activeTab: 'calc',
      // ── Calculator ──
      previous: null, current: '', operator: null, operatorClicked: false,
      expression: '', history: [],
      // ── Relay ──
      relayDifficulties: RELAY_DIFFICULTIES, relayDifficulty: 'Easy', relayConfig: RELAY_DIFFICULTIES[0],
      relayState: 'idle', relayStart: 0, relaySteps: [], currentStepIndex: 0,
      confirmedValue: 0, relayUserInput: '', relayFeedback: '', relayFeedbackClass: '',
      // ── Speed Round ──
      speedDifficulties: SPEED_DIFFICULTIES, speedDifficulty: 'Easy', speedConfig: SPEED_DIFFICULTIES[0],
      speedState: 'idle', speedQuestion: '', speedCorrect: 0, speedScore: 0,
      speedUserInput: '', speedFeedback: '', speedFeedbackClass: '',
      timeLeft: 60, speedTimer: null,
      // ── Target Number ──
      targetDifficulties: TARGET_DIFFICULTIES, targetDifficulty: 'Easy', targetConfig: TARGET_DIFFICULTIES[0],
      targetState: 'idle', targetGoal: 0, targetNumbers: [], targetStart: 0,
      targetPressesLeft: 0, targetUserInput: '', targetFeedback: '', targetFeedbackClass: '',
      // ── Broken Calc ──
      brokenDifficulties: BROKEN_DIFFICULTIES, brokenDifficulty: 'Easy', brokenConfig: BROKEN_DIFFICULTIES[0],
      brokenState: 'idle', brokenTarget: 0, brokenKeys: [],
      brokenCurrent: '', brokenPrevious: null, brokenOperator: null, brokenOpClicked: false,
      brokenFeedback: '', brokenFeedbackClass: '',
    };
  },

  computed: {
    timerPercent() { return (this.timeLeft / this.speedConfig.time) * 100; },
  },
  methods: {
    // ══ Calculator ══
    handleKeydown(e) {
      if (this.activeTab !== 'calc') return;
      const k = e.key;
      if (k >= '0' && k <= '9') { this.append(k); return; }
      if (k === '.') { this.dot(); return; }
      if (k === '+') { this.add(); return; }
      if (k === '-') { this.minus(); return; }
      if (k === '*') { this.times(); return; }
      if (k === '/') { e.preventDefault(); this.divide(); return; }
      if (k === 'Enter' || k === '=') { this.equal(); return; }
      if (k === 'Backspace') { this.current = this.current.slice(0, -1); return; }
      if (k === 'Escape') { this.clearAll(); return; }
      if (k === '%') { this.percent(); return; }
    },
    clearAll() { this.current = ''; this.previous = null; this.operator = null; this.operatorClicked = false; this.expression = ''; },
    sign() { if (!this.current) return; this.current = this.current.charAt(0) === '-' ? this.current.slice(1) : `-${this.current}`; },
    percent() { this.current = `${parseFloat(this.current) / 100}`; },
    append(n) {
      if (this.operatorClicked) { this.current = ''; this.operatorClicked = false; }
      if (this.current.length > 12) return;
      this.current = `${this.current}${n}`;
    },
    dot() { if (this.current.indexOf('.') === -1) this.append('.'); },
    setPrevious(sym) { this.expression = `${this.current} ${sym}`; this.previous = this.current; this.operatorClicked = true; },
    divide() { this.operator = (a,b) => b === 0 ? 'ERR' : a / b; this.setPrevious('÷'); },
    times()  { this.operator = (a,b) => a * b; this.setPrevious('×'); },
    minus()  { this.operator = (a,b) => a - b; this.setPrevious('−'); },
    add()    { this.operator = (a,b) => a + b; this.setPrevious('+'); },
    equal() {
      if (!this.operator || this.previous === null) return;
      const a = parseFloat(this.previous), b = parseFloat(this.current);
      const result = this.operator(b, a);
      const expr = `${this.expression} ${this.current} = ${result === 'ERR' ? 'Error: ÷0' : result}`;
      this.history.unshift(expr);
      if (this.history.length > 8) this.history.pop();
      this.current = result === 'ERR' ? 'Error: ÷0' : `${result}`;
      this.expression = '';
      this.previous = null;
      this.operator = null;
    },

    // ══ Relay ══
    setRelayDifficulty(d) { this.relayDifficulty = d.label; this.relayConfig = d; this.relayState = 'idle'; },
    startRelay() {
      const cfg = this.relayConfig;
      const start = rand(cfg.startMin, cfg.startMax);
      this.relayStart = start; this.confirmedValue = start;
      this.currentStepIndex = 0; this.relayUserInput = ''; this.relayFeedback = '';
      const steps = []; let cur = start;
      for (let i = 0; i < cfg.steps; i++) {
        let op, operand, result, tries = 0;
        do {
          op = cfg.ops[Math.floor(Math.random() * cfg.ops.length)];
          if (op === '÷') {
            const divs = [];
            for (let d = 2; d <= Math.min(cur, 20); d++) { if (cur % d === 0) divs.push(d); }
            if (!divs.length) { op = '+'; operand = rand(cfg.numMin, cfg.numMax); }
            else operand = divs[Math.floor(Math.random() * divs.length)];
          } else { operand = rand(cfg.numMin, cfg.numMax); }
          result = applyOp(cur, op, operand); tries++;
        } while ((result <= 0 || !Number.isFinite(result)) && tries < 20);
        steps.push({ opSymbol: op, operand, before: cur, result });
        cur = result;
      }
      this.relaySteps = steps; this.relayState = 'playing';
      this.$nextTick(() => { if (this.$refs.relayInput) this.$refs.relayInput.focus(); });
    },
    checkRelayStep() {
      const val = parseFloat(this.relayUserInput);
      const step = this.relaySteps[this.currentStepIndex];
      if (isNaN(val)) { this.relayFeedback = 'Please enter a number.'; this.relayFeedbackClass = 'feedback-wrong'; return; }
      if (Math.abs(val - step.result) < 0.001) {
        this.confirmedValue = step.result;
        this.relayFeedback = '✓ Correct!'; this.relayFeedbackClass = 'feedback-correct';
        this.relayUserInput = '';
        setTimeout(() => {
          this.relayFeedback = '';
          if (this.currentStepIndex + 1 >= this.relaySteps.length) {
            this.relayState = 'complete';
            swal('Relay complete! Great work! 🎉', { closeOnClickOutside: false });
          } else {
            this.currentStepIndex++;
            this.$nextTick(() => { if (this.$refs.relayInput) this.$refs.relayInput.focus(); });
          }
        }, 700);
      } else {
        this.relayFeedback = '✗ Not quite, try again.'; this.relayFeedbackClass = 'feedback-wrong'; this.relayUserInput = '';
      }
    },

    // ══ Speed Round ══
    setSpeedDifficulty(d) { this.speedDifficulty = d.label; this.speedConfig = d; this.speedState = 'idle'; },
    switchTab(tab) {
      if (this.speedTimer) { clearInterval(this.speedTimer); this.speedTimer = null; }
      this.speedState = 'idle';
      this.activeTab = tab;
    },
    generateSpeedQ() {
      const cfg = this.speedConfig;
      const op = cfg.ops[Math.floor(Math.random() * cfg.ops.length)];
      let a, b, ans;
      if (op === '÷') {
        b = rand(2, 12); ans = rand(1, 12); a = b * ans;
      } else if (op === '×') {
        a = rand(cfg.min, Math.min(cfg.max, 12)); b = rand(cfg.min, Math.min(cfg.max, 12)); ans = a * b;
      } else if (op === '−') {
        a = rand(cfg.min + 5, cfg.max); b = rand(cfg.min, a - 1); ans = a - b;
      } else {
        a = rand(cfg.min, cfg.max); b = rand(cfg.min, cfg.max); ans = a + b;
      }
      const sym = op === '÷' ? '÷' : op === '×' ? '×' : op === '−' ? '−' : '+';
      this.speedQuestion = `What is ${a} ${sym} ${b}?`;
      this.speedCorrect = ans;
    },
    startSpeed() {
      if (this.speedTimer) clearInterval(this.speedTimer);
      this.speedScore = 0; this.speedUserInput = ''; this.speedFeedback = '';
      this.timeLeft = this.speedConfig.time;
      this.generateSpeedQ();
      this.speedState = 'playing';
      this.speedTimer = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) {
          clearInterval(this.speedTimer); this.speedTimer = null;
          this.speedState = 'done';
          swal(`Time's up! You scored ${this.speedScore} 🏁`, { closeOnClickOutside: false });
        }
      }, 1000);
      this.$nextTick(() => { if (this.$refs.speedInput) this.$refs.speedInput.focus(); });
    },
    checkSpeed() {
      const val = parseFloat(this.speedUserInput);
      if (isNaN(val)) return;
      if (Math.abs(val - this.speedCorrect) < 0.001) {
        this.speedScore++;
        this.speedFeedback = '✓ Correct!'; this.speedFeedbackClass = 'feedback-correct';
        this.speedUserInput = '';
        setTimeout(() => { this.speedFeedback = ''; this.generateSpeedQ();
          this.$nextTick(() => { if (this.$refs.speedInput) this.$refs.speedInput.focus(); }); }, 400);
      } else {
        this.speedFeedback = '✗ Wrong!'; this.speedFeedbackClass = 'feedback-wrong';
        this.speedUserInput = '';
        this.$nextTick(() => { if (this.$refs.speedInput) this.$refs.speedInput.focus(); });
      }
    },

    // ══ Target Number ══
    setTargetDifficulty(d) { this.targetDifficulty = d.label; this.targetConfig = d; this.targetState = 'idle'; },
    startTarget() {
      const cfg = this.targetConfig;
      this.targetGoal = rand(cfg.targetMin, cfg.targetMax);
      const nums = new Set();
      while (nums.size < cfg.numbersCount) nums.add(rand(cfg.min, cfg.max));
      this.targetNumbers = [...nums];
      this.targetStart = this.targetNumbers[0];
      this.targetPressesLeft = cfg.presses;
      this.targetUserInput = ''; this.targetFeedback = '';
      this.targetState = 'playing';
      this.$nextTick(() => { if (this.$refs.targetInput) this.$refs.targetInput.focus(); });
    },
    checkTarget() {
      const val = parseFloat(this.targetUserInput);
      if (isNaN(val)) { this.targetFeedback = 'Enter a number.'; this.targetFeedbackClass = 'feedback-wrong'; return; }
      this.targetPressesLeft--;
      if (Math.abs(val - this.targetGoal) < 0.001) {
        this.targetState = 'won';
        swal(`🎯 Target hit! You reached ${this.targetGoal}!`, { closeOnClickOutside: false });
      } else if (this.targetPressesLeft <= 0) {
        this.targetState = 'lost';
      } else {
        this.targetFeedback = `✗ Not yet. ${this.targetPressesLeft} press${this.targetPressesLeft !== 1 ? 'es' : ''} left.`;
        this.targetFeedbackClass = 'feedback-wrong'; this.targetUserInput = '';
        this.$nextTick(() => { if (this.$refs.targetInput) this.$refs.targetInput.focus(); });
      }
    },

    // ══ Broken Calculator ══
    setBrokenDifficulty(d) { this.brokenDifficulty = d.label; this.brokenConfig = d; this.brokenState = 'idle'; },
    startBroken() {
      const cfg = this.brokenConfig;
      this.brokenTarget = rand(cfg.targetMin, cfg.targetMax);
      const pool = [...BREAKABLE];
      const broken = [];
      while (broken.length < cfg.brokenCount) {
        const idx = Math.floor(Math.random() * pool.length);
        broken.push(pool.splice(idx, 1)[0]);
      }
      this.brokenKeys = broken;
      this.brokenCurrent = ''; this.brokenPrevious = null; this.brokenOperator = null; this.brokenOpClicked = false;
      this.brokenFeedback = ''; this.brokenState = 'playing';
    },
    isBroken(key) { return this.brokenKeys.includes(key); },
    bCalcAppend(n) {
      if (this.isBroken(n)) return;
      if (this.brokenOpClicked) { this.brokenCurrent = ''; this.brokenOpClicked = false; }
      if (this.brokenCurrent.length > 10) return;
      this.brokenCurrent = `${this.brokenCurrent}${n}`;
    },
    bCalcOp(sym) {
      if (this.isBroken(sym)) return;
      const map = { '÷': (a,b) => b===0?null:a/b, '×': (a,b)=>a*b, '−': (a,b)=>a-b, '+': (a,b)=>a+b };
      this.brokenOperator = map[sym]; this.brokenPrevious = this.brokenCurrent; this.brokenOpClicked = true;
    },
    bCalcEqual() {
      if (!this.brokenOperator || this.brokenPrevious === null) return;
      const a = parseFloat(this.brokenPrevious), b = parseFloat(this.brokenCurrent);
      const r = this.brokenOperator(b, a);
      this.brokenCurrent = r === null ? 'ERR' : `${r}`;
      this.brokenPrevious = null; this.brokenOperator = null;
    },
    bCalcFn(fn) {
      if (fn === 'clear') { this.brokenCurrent = ''; this.brokenPrevious = null; this.brokenOperator = null; this.brokenOpClicked = false; }
      else if (fn === 'sign') { if (!this.isBroken('+/-')) this.brokenCurrent = this.brokenCurrent.startsWith('-') ? this.brokenCurrent.slice(1) : `-${this.brokenCurrent}`; }
      else if (fn === 'pct')  { if (!this.isBroken('%')) this.brokenCurrent = `${parseFloat(this.brokenCurrent)/100}`; }
    },
    bCalcDot() { if (!this.isBroken('.') && this.brokenCurrent.indexOf('.') === -1) this.brokenCurrent += '.'; },
    checkBroken() {
      const val = parseFloat(this.brokenCurrent);
      if (isNaN(val)) { this.brokenFeedback = 'Calculate a result first.'; this.brokenFeedbackClass = 'feedback-wrong'; return; }
      if (Math.abs(val - this.brokenTarget) < 0.001) {
        this.brokenState = 'won';
        swal(`🔧 Brilliant! You reached ${this.brokenTarget} with broken keys!`, { closeOnClickOutside: false });
      } else {
        this.brokenFeedback = `✗ Got ${val}, need ${this.brokenTarget}. Keep trying!`;
        this.brokenFeedbackClass = 'feedback-wrong';
      }
    },
  },
  beforeDestroy() {
    if (this.speedTimer) clearInterval(this.speedTimer);
  },
};
</script>

<style scoped>
* { text-align: center; font-size: 40px; }
.calc-root { outline: none; }
.topcalc { margin-top: 18px; }

/* Tab bar */
.tab-bar { display:flex; justify-content:center; flex-wrap:wrap; gap:0; margin:16px auto 20px; width:fit-content; border:1px solid rgb(3,53,48); border-radius:8px; overflow:hidden; }
.tab-btn  { padding:8px 20px; font-size:15px; border:none; border-right:1px solid rgb(3,53,48); background:transparent; color:rgb(3,53,48); cursor:pointer; transition:background .15s; }
.tab-btn:last-child { border-right:none; }
.tab-btn:hover { background:rgb(169,247,234); }
.tab-active { background:rgb(3,53,48)!important; color:white; }

/* Calculator */
.calc-tab-content { max-width: 480px; margin: 0 auto; }
.calculator { display:grid; grid-template-columns:repeat(4,1fr); grid-auto-rows:minmax(50px,auto); }
.display-expr { grid-column:1/5; background:#555; color:#ccc; font-size:16px; min-height:24px; padding:2px 8px; text-align:right; }
.display { grid-column:1/5; background:#333; color:#fff; overflow-x:auto; word-wrap:break-word; text-align:right; padding:4px 10px; }
.history-panel { grid-column:1/5; background:#f9f9f9; border-bottom:1px solid #ddd; max-height:120px; overflow-y:auto; }
.history-row { font-size:13px; color:#666; padding:2px 8px; text-align:right; border-bottom:1px solid #eee; }
.zero { grid-column:1/3; }
.btn { background:#eee; border:1px solid #999; cursor:pointer; }
.btn:hover { background:#ddd; }
.btn-fn { background:#c0c0c0; }
.operator { background:orange; color:white; }

.calcbord { border:2px solid black; margin:30px 0; border-radius:15px; padding:16px 40px 20px; }
.tips-list { list-style:none; padding:0; text-align:left; }
.tips-list li { font-size:17px; line-height:1.8; border-bottom:1px solid rgba(0,0,0,.07); padding:4px 0; }

/* Shared game layout */
.game-wrapper { max-width:580px; margin:0 auto; padding:0 12px 60px; }
.pill-row { display:flex; justify-content:center; gap:8px; margin-bottom:20px; flex-wrap:wrap; }
.pill-btn { border-radius:20px; padding:4px 18px; font-size:14px; cursor:pointer; border:1px solid rgb(3,53,48); background:transparent; color:rgb(3,53,48); transition:all .15s; }
.pill-btn:hover { background:rgb(169,247,234); }
.pill-btn.active { background:rgb(3,53,48); color:white; }

.game-card { border:2px dashed rgba(0,0,0,.2); border-radius:14px; padding:24px 20px; background:rgba(169,247,234,.12); text-align:center; }
.game-card-title { font-size:22px; color:rgb(3,53,48); margin-bottom:12px; }
.game-p { font-size:16px; line-height:1.7; margin-bottom:10px; color:#333; }
.game-start-btn { margin-top:16px; background:rgb(3,53,48); color:white; padding:10px 36px; font-size:16px; border:none; border-radius:8px; cursor:pointer; }
.game-start-btn:hover { background:rgb(5,80,70); }
.game-complete { background:rgba(169,247,234,.25); }
.game-over { background:rgba(255,200,200,.25); }
.complete-icon { font-size:52px; margin-bottom:8px; }

.answer-row { display:flex; justify-content:center; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:10px; }
.game-input { width:150px; height:48px; font-size:22px; text-align:center; border:2px solid rgb(3,53,48); border-radius:8px; outline:none; padding:4px 8px; }
.game-input:focus { border-color:rgb(3,120,70); background:#f0fdf8; }
.game-check-btn { height:48px; padding:0 22px; font-size:16px; background:rgb(3,53,48); color:white; border:none; border-radius:8px; cursor:pointer; }
.game-check-btn:hover { background:rgb(5,80,70); }
.game-feedback { font-size:15px; font-weight:bold; margin-top:6px; }
.feedback-correct { color:rgb(3,100,50); }
.feedback-wrong   { color:rgb(160,30,20); }
.btn-skip { background:transparent; border:1px solid #aaa; color:#666; border-radius:8px; padding:6px 20px; font-size:14px; cursor:pointer; }
.btn-skip:hover { background:#f0f0f0; }

/* Relay */
.step-progress { display:flex; justify-content:center; gap:10px; margin-bottom:18px; }
.step-dot { width:14px; height:14px; border-radius:50%; border:2px solid rgb(3,53,48); transition:background .2s; }
.dot-done    { background:rgb(3,53,48); }
.dot-current { background:orange; border-color:orange; }
.dot-pending { background:transparent; }
.relay-start-num { margin-bottom:16px; }
.relay-label { font-size:14px; color:#666; display:block; margin-bottom:2px; }
.relay-big-num { font-size:48px; font-weight:bold; color:rgb(3,53,48); line-height:1; }
.step-instruction { margin-bottom:20px; }
.step-counter { font-size:13px; color:#888; display:block; margin-bottom:10px; text-transform:uppercase; letter-spacing:.5px; }
.step-op-display { display:flex; align-items:center; justify-content:center; gap:12px; margin-bottom:8px; }
.step-op-symbol { font-size:40px; font-weight:bold; color:orange; line-height:1; }
.step-op-num    { font-size:40px; font-weight:bold; color:rgb(3,53,48); line-height:1; }
.step-hint { font-size:15px; color:#555; margin:0; }
.summary-table { margin:16px 0; border:1px solid rgba(0,0,0,.1); border-radius:8px; overflow:hidden; }
.summary-row { display:flex; justify-content:space-between; padding:8px 16px; font-size:15px; border-bottom:1px solid rgba(0,0,0,.07); background:rgba(255,255,255,.6); }
.summary-row:last-child { border-bottom:none; }
.summary-step { color:#888; font-size:13px; align-self:center; }
.summary-expr { font-weight:bold; color:rgb(3,53,48); }

/* Speed Round */
.timer-track { height:12px; background:#e0e0e0; border-radius:6px; margin-bottom:6px; overflow:hidden; }
.timer-fill  { height:100%; background:rgb(3,53,48); border-radius:6px; transition:width 1s linear; }
.timer-warn  { background:rgb(200,50,50)!important; }
.timer-label { font-size:18px; font-weight:bold; color:rgb(3,53,48); margin-bottom:12px; }
.speed-question { font-size:26px; font-weight:bold; color:rgb(3,53,48); margin-bottom:18px; }
.speed-score-live { font-size:16px; color:#555; margin-top:10px; }

/* Target Number */
.target-meta { display:flex; justify-content:center; gap:24px; margin-bottom:16px; flex-wrap:wrap; }
.target-box  { display:flex; flex-direction:column; align-items:center; background:rgb(169,247,234); border-radius:10px; padding:10px 24px; border:1px solid rgba(3,53,48,.3); }
.target-label { font-size:13px; color:#555; text-transform:uppercase; letter-spacing:.5px; }
.target-num   { font-size:38px; font-weight:bold; color:rgb(3,53,48); line-height:1.2; }
.presses-warn { color:rgb(180,30,30)!important; }

/* Broken Calculator */
.broken-calc { margin-top:16px; max-width:320px; margin-left:auto; margin-right:auto; }
.broken { background:rgb(80,80,80)!important; color:#555!important; cursor:not-allowed!important; opacity:.45; }
.broken:hover { background:rgb(80,80,80)!important; }
.broken-key-badge { display:inline-block; background:rgb(220,50,50); color:white; border-radius:6px; padding:2px 10px; font-size:16px; margin:0 4px; font-weight:bold; }

/* Animations */
.fade-enter-active,.fade-leave-active { transition:opacity .3s; }
.fade-enter,.fade-leave-to { opacity:0; }
</style>
